import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserRole } from '../../hooks/useUserRole';
import { useSidebar } from '../../context/SidebarContext';
import './ActivityBar.css';

const ActivityBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOwner } = useUserRole();
  const {
    isPinned,
    togglePinned,
    activeGroup,
    setActiveGroup,
    setHasMultipleItems,
    closeMobileMenu,
  } = useSidebar();
  const sidebarRef = useRef(null);
  const [submenuTop, setSubmenuTop] = useState(48);
  const closeTimerRef = useRef(null);
  const [clickedOpen, setClickedOpen] = useState(false);

  // Navigation groups with CTA actions (dynamic based on section)
  const navGroups = [
    {
      id: 'dashboard',
      icon: 'dashboard',
      label: t('nav.dashboard', 'Dashboard'),
      items: [{ path: '/', label: t('nav.dashboard', 'Dashboard'), icon: 'dashboard' }],
    },
    {
      id: 'menu',
      icon: 'restaurant_menu',
      label: t('nav.menu', 'Menú'),
      items: [
        { path: '/menu', label: t('nav.menu', 'Carta'), icon: 'menu_book' },
        { path: '/menu/modifiers', label: t('nav.modifiers', 'Modificadores'), icon: 'tune' },
      ],
      cta: {
        label: t('actions.newProduct', 'Nuevo Producto'),
        icon: 'add',
        action: () => navigate('/menu?action=new'),
      },
    },
    {
      id: 'operations',
      icon: 'receipt_long',
      label: t('nav.operations', 'Operaciones'),
      items: [
        { path: '/orders', label: t('nav.orders', 'Pedidos'), icon: 'receipt_long' },
        { path: '/tables', label: t('nav.tables', 'Mesas'), icon: 'table_restaurant' },
      ],
      cta: {
        label: t('actions.newOrder', 'Nuevo Pedido'),
        icon: 'add',
        action: () => navigate('/orders?action=new'),
      },
    },
    {
      id: 'delivery',
      icon: 'two_wheeler',
      label: t('nav.delivery', 'Delivery'),
      items: [
        {
          path: '/delivery/integrations',
          label: t('nav.deliveryIntegrations', 'Integraciones'),
          icon: 'sync_alt',
        },
      ],
    },
    {
      id: 'finance',
      icon: 'payments',
      label: t('nav.finance', 'Finanzas'),
      items: [
        { path: '/cash-register', label: t('nav.cashRegister', 'Caja'), icon: 'point_of_sale' },
        { path: '/invoicing', label: t('nav.invoicing', 'Facturación'), icon: 'receipt_long' },
        { path: '/expenses', label: t('nav.expenses', 'Gastos'), icon: 'receipt' },
        ...(isOwner
          ? [
              {
                path: '/fiscal-config',
                label: t('nav.fiscalConfig', 'Config Fiscal'),
                icon: 'verified',
              },
            ]
          : []),
      ],
      cta: {
        label: t('actions.newInvoice', 'Nueva Factura'),
        icon: 'add',
        action: () => navigate('/invoicing'),
      },
    },
    {
      id: 'inventory',
      icon: 'inventory_2',
      label: t('nav.inventory', 'Inventario'),
      items: [
        { path: '/inventory', label: t('nav.inventory', 'Stock'), icon: 'inventory_2' },
        {
          path: '/inventory/movements',
          label: t('nav.movements', 'Movimientos'),
          icon: 'swap_vert',
        },
        {
          path: '/inventory/suppliers',
          label: t('nav.suppliers', 'Proveedores'),
          icon: 'local_shipping',
        },
        {
          path: '/inventory/purchase-orders',
          label: t('nav.purchaseOrders', 'Órdenes de Compra'),
          icon: 'shopping_cart',
        },
      ],
      cta: {
        label: t('actions.newItem', 'Nuevo Item'),
        icon: 'add',
        action: () => navigate('/inventory?action=new'),
      },
    },
    {
      id: 'customers',
      icon: 'people',
      label: t('nav.customers', 'Clientes'),
      items: [{ path: '/customers', label: t('nav.customers', 'Clientes'), icon: 'people' }],
      cta: {
        label: t('actions.newCustomer', 'Nuevo Cliente'),
        icon: 'add',
        action: () => navigate('/customers?action=new'),
      },
    },
    {
      id: 'config',
      icon: 'settings',
      label: t('nav.config', 'Configuración'),
      items: [
        { path: '/restaurants', label: t('nav.restaurants', 'Restaurantes'), icon: 'store' },
        ...(isOwner
          ? [{ path: '/users', label: t('nav.users', 'Usuarios'), icon: 'manage_accounts' }]
          : []),
        ...(isOwner
          ? [
              {
                path: '/subscription',
                label: t('nav.subscription', 'Suscripción'),
                icon: 'card_membership',
              },
            ]
          : []),
        ...(isOwner
          ? [{ path: '/domains', label: t('nav.domains', 'Dominios'), icon: 'language' }]
          : []),
        ...(isOwner
          ? [{ path: '/partners', label: t('nav.partners', 'Partners'), icon: 'handshake' }]
          : []),
        { path: '/settings', label: t('nav.settings', 'Ajustes'), icon: 'settings' },
      ],
    },
  ];

  // Get current active group based on path
  const getCurrentGroup = useCallback(() => {
    for (const group of navGroups) {
      for (const item of group.items) {
        if (location.pathname === item.path || location.pathname.startsWith(item.path + '/')) {
          return group.id;
        }
      }
    }
    return 'dashboard';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const currentActiveGroup = getCurrentGroup();

  // Close sidebar when clicking outside (only in floating mode)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isPinned && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        // Check if click is on activity bar icon
        const isActivityBarClick = event.target.closest('.activity-bar-item');
        if (!isActivityBarClick) {
          setActiveGroup(null);
          setHasMultipleItems(false);
          setClickedOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPinned, setActiveGroup, setHasMultipleItems]);

  // Handle icon hover (when sidebar is floating/not pinned)
  const handleGroupHover = (group, event) => {
    // Only show submenu on hover if sidebar is not pinned (floating mode)
    if (!isPinned && group.items.length > 1) {
      // Calculate the position of the button
      const buttonRect = event.currentTarget.getBoundingClientRect();
      let top = buttonRect.top;

      // Estimate submenu height (roughly 48px per item + header)
      const estimatedHeight = group.items.length * 48 + 48;
      const maxTop = window.innerHeight - estimatedHeight - 16;

      // Adjust if it would go off screen
      if (top + estimatedHeight > window.innerHeight - 16) {
        top = Math.max(8, maxTop);
      }

      setSubmenuTop(top);
      setActiveGroup(group.id);
      setHasMultipleItems(true);
    }
  };

  // Handle mouse leave from activity bar area
  const handleActivityBarLeave = () => {
    // Close submenu after a small delay when mouse leaves if not pinned
    // But keep it open if it was explicitly clicked open
    if (!isPinned && !clickedOpen) {
      closeTimerRef.current = setTimeout(() => {
        setActiveGroup(null);
        setHasMultipleItems(false);
      }, 300); // 300ms delay
    }
  };

  // Handle mouse enter to activity bar - cancel close timer
  const handleActivityBarEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Handle mouse enter to submenu - cancel close timer
  const handleSubmenuEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Handle mouse leave from submenu
  const handleSubmenuLeave = () => {
    if (!isPinned && !clickedOpen) {
      closeTimerRef.current = setTimeout(() => {
        setActiveGroup(null);
        setHasMultipleItems(false);
      }, 300); // 300ms delay
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  // Handle icon click
  const handleGroupClick = (group) => {
    // If single item group, navigate directly without showing submenu
    if (group.items.length === 1) {
      navigate(group.items[0].path);
      closeMobileMenu();
      setActiveGroup(null);
      setHasMultipleItems(false);
      setClickedOpen(false);
      return;
    }

    // Toggle submenu — if already open via click, close it
    if (activeGroup === group.id && clickedOpen) {
      setActiveGroup(null);
      setHasMultipleItems(false);
      setClickedOpen(false);
      return;
    }

    // Open submenu persistently (won't close on mouse leave)
    setActiveGroup(group.id);
    setHasMultipleItems(true);
    setClickedOpen(true);
    if (activeGroup !== group.id) {
      navigate(group.items[0].path);
    }
  };

  // Handle submenu item click — keep submenu open so user can browse items
  const handleItemClick = (path) => {
    navigate(path);
    closeMobileMenu();
  };

  // Close secondary sidebar
  const closeSidebar = () => {
    if (!isPinned) {
      setActiveGroup(null);
      setHasMultipleItems(false);
      setClickedOpen(false);
    }
  };

  // Get the current group data
  const activeGroupData = navGroups.find((g) => g.id === activeGroup);
  const showSecondarySidebar = activeGroup && activeGroupData && activeGroupData.items.length > 1;

  return (
    <>
      {/* Mobile backdrop for activity bar drawer */}
      <div className="mobile-menu-backdrop" onClick={closeMobileMenu} />
      <div
        className="activity-bar"
        onMouseLeave={handleActivityBarLeave}
        onMouseEnter={handleActivityBarEnter}
      >
        {/* Menu Toggle Button - Pin/Unpin sidebar */}
        <div className="activity-bar-logo">
          <button
            type="button"
            className={`activity-bar-menu-btn ${isPinned ? 'pinned' : ''}`}
            onClick={togglePinned}
            title={isPinned ? t('common.unpin', 'Despinear menú') : t('common.pin', 'Pinear menú')}
            aria-label={
              isPinned ? t('common.unpin', 'Despinear menú') : t('common.pin', 'Pinear menú')
            }
          >
            <span className="material-icons">menu</span>
          </button>
        </div>

        {/* Navigation Icons */}
        <div className="activity-bar-icons">
          {navGroups.map((group) => (
            <div key={group.id} className="activity-bar-item-wrapper">
              <button
                type="button"
                className={`activity-bar-item ${currentActiveGroup === group.id ? 'active' : ''} ${activeGroup === group.id ? 'flyout-open' : ''}`}
                onClick={() => handleGroupClick(group)}
                onMouseEnter={(e) => handleGroupHover(group, e)}
                title={group.label}
                aria-label={group.label}
              >
                <span className="material-icons">{group.icon}</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Backdrop for floating mode */}
      {showSecondarySidebar && !isPinned && (
        <div className="sidebar-backdrop" onClick={closeSidebar} />
      )}

      {/* Secondary Sidebar */}
      {showSecondarySidebar && (
        <div
          ref={sidebarRef}
          className={`secondary-sidebar ${isPinned ? 'pinned' : 'floating'}`}
          style={!isPinned ? { top: `${submenuTop}px` } : {}}
          onMouseEnter={handleSubmenuEnter}
          onMouseLeave={handleSubmenuLeave}
        >
          <div className="secondary-sidebar-header">
            <span className="secondary-sidebar-title">{activeGroupData.label}</span>
            <div className="secondary-sidebar-actions">
              {/* Close Button (only in floating mode) */}
              {!isPinned && (
                <button
                  type="button"
                  className="sidebar-action-btn"
                  onClick={closeSidebar}
                  title={t('common.close', 'Cerrar')}
                  aria-label={t('common.close', 'Cerrar')}
                >
                  <span className="material-icons">close</span>
                </button>
              )}
            </div>
          </div>

          <div className="secondary-sidebar-items">
            {activeGroupData.items.map((item) => (
              <button
                type="button"
                key={item.path}
                className={`secondary-sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleItemClick(item.path)}
              >
                <span className="material-icons">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* CTA Button - Dynamic based on section - In container */}
          {activeGroupData.cta && (
            <div className="secondary-sidebar-footer">
              <button
                type="button"
                className="sidebar-cta-btn-full"
                onClick={activeGroupData.cta.action}
                title={activeGroupData.cta.label}
              >
                <span className="material-icons">{activeGroupData.cta.icon}</span>
                <span>{activeGroupData.cta.label}</span>
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ActivityBar;
